import { BrButton, BrDivider, BrInput, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import AnmSelectQuery from 'components/anmSelectQuery/anmSelectQuery';
import AtualizarInformacoes from 'components/atualizarInformacoes/atualizarInformacoes';
import ErrorMessage from 'components/errorMessage/errorMessage';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { useNotificacao } from 'hooks/useNotificacao';
import {
  IBalneario,
  IBalnearioComBanhoSalvar,
  IBalnearioInformacoesComBanhoEnviar,
  IBalnearioInformacoesSemBanhoEnviar,
  IBalnearioSemBanhoSalvar,
} from 'interfaces/IBalneario';
import { IDeducoes } from 'interfaces/IDeducao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { INcmListagem } from 'interfaces/INcm';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import { converterDeducoesParaArrayString } from 'utils/converterDeducoesParaString';
import { formatarCompetenciaPorMesEAno } from 'utils/formatCompetencia';
import { formatCurrency, removeFormatCurrency } from 'utils/formatCurrency';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { getHintText } from 'utils/getHintText';
import styles from './balnearioForm.module.scss';
import { validarCamposObrigatoriosComBanho, validarCamposObrigatoriosSemBanho } from './balnearioValidacoes';

interface IBalnearioProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  comBanho: boolean;
  setIsAdicaoNovaBaseCalculo: React.Dispatch<React.SetStateAction<boolean>>;
}

const BalnearioForm = ({ isEdicao, setIsEdicao, comBanho, setIsAdicaoNovaBaseCalculo }: IBalnearioProps) => {
  const VALOR_MINIMO = 0.01;
  const VALOR_MAXIMO = 1e10; //10 Digitos

  const [codigoNCM, setCodigoNCM] = useState<string>('');
  const [hasPrecoBanhoEspecificado, setHasPrecoBanhoEspecificado] = useState<boolean>(comBanho);
  const [valorBanho, setValorBanho] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');
  const [receitaBrutaEstabelecimento, setReceitaBrutaEstabelecimento] = useState<string>('');
  const [haCompraTerceiro, setHaCompraTerceiro] = useState<boolean>(false);
  const [deducoesAdicionadas, setDeducoesAdicionadas] = useState<{ nome: string; valor: number }[]>([]);

  const [isCalculoInvalido, setIsCalculoInvalido] = useState<boolean>(false);

  const [isLoadingInfo, setIsLoadingInfo] = useState<boolean>(false);

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const [infoSemBanho, setInfoSemBanho] = useState([{ label: 'Base de Cálculo(R$)', value: 0 }]);
  const [infoComBanho, setInfoComBanho] = useState([{ label: 'Base de Cálculo(R$)', value: 0 }]);

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const {
    getDeducoesBalneario,
    getBalneario,
    postSalvarBalnearioComBanho,
    postSalvarBalnearioSemBanho,
    putAtualizarBalnearioComBanho,
    putAtualizarBalnearioSemBanho,
    postReceberInformacaoBalnearioSemBanho,
    postReceberInformacaoBalnearioComBanho,
    getNcmPorGuia,
  } = useBackEndRequests();

  const context = useContext(ArrecadacaoContext);
  const idBalneario = context?.idBalneario ?? -1;
  const competencia = formatarCompetenciaPorMesEAno(context?.competenciaMes || 0, context?.competenciaAno || 0);

  const mostrarNotificacao = useNotificacao();

  const {
    data: deducoesData,
    isLoading: deducoesIsLoading,
    error: deducoesError,
  } = useQuery<IDeducoes, IErrorWithSentryId>({
    queryKey: ['deducoesBalnearioQuery'],
    queryFn: () => getDeducoesBalneario(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
  });

  const {
    data: balnearioData,
    isLoading: balnearioDataLoading,
    error: balnearioDataError,
  } = useQuery<IBalneario, IErrorWithSentryId>({
    queryKey: ['getBalneario', comBanho, idBalneario, codigoNCM],
    queryFn: () => getBalneario(comBanho, idBalneario),
    refetchOnWindowFocus: false,
    enabled: isEdicao && idBalneario !== -1,
  });

  useEffect(() => {
    if (balnearioData && isEdicao) {
      if (comBanho) {
        setCodigoNCM(balnearioData?.codigoNcm || '');
        setValorBanho(formatCurrency(balnearioData?.valorBanho ?? 0));
        setQuantidade(balnearioData?.qtdeBanho?.toString() ?? '');
      } else {
        setCodigoNCM(balnearioData?.codigoNcm || '');
        setReceitaBrutaEstabelecimento(formatCurrency(balnearioData?.valorReceitaBruta ?? 0));
        setDeducoesAdicionadas(balnearioData?.tributos || []);
      }
    }
  }, [balnearioData, comBanho, isEdicao]);

  useEffect(() => {
    setIsCalculoInvalido(invalidarCalculo());
  }, [hasPrecoBanhoEspecificado, valorBanho, quantidade, receitaBrutaEstabelecimento]);

  const tributosIncidentes = formatCurrency(deducoesAdicionadas.reduce((soma, tributo) => soma + tributo.valor, 0));

  const invalidarCampoFinanceiro = (valor: string) => {
    if (valor === '') {
      return false;
    }

    const valorSemMascara = removeFormatCurrency(valor);

    return valorSemMascara < VALOR_MINIMO || valorSemMascara >= VALOR_MAXIMO;
  };

  const invalidarCampoNumero = (valor: string) => {
    if (valor === '') {
      return false;
    }

    const valorSemMascara = parseFloat(valor);

    return valorSemMascara < VALOR_MINIMO || valorSemMascara >= VALOR_MAXIMO;
  };

  const invalidarCalculo = () => {
    if (hasPrecoBanhoEspecificado) {
      const isValorBanhoInvalido = invalidarCampoFinanceiro(valorBanho);
      const isQuantidadeInvalida = invalidarCampoNumero(quantidade);

      return isValorBanhoInvalido || isQuantidadeInvalida;
    } else {
      const isReceitaBrutaInvalida = invalidarCampoFinanceiro(receitaBrutaEstabelecimento);

      return isReceitaBrutaInvalida;
    }
  };

  const receitaLiquidaEstabelecimento = formatCurrency(
    removeFormatCurrency(receitaBrutaEstabelecimento) - removeFormatCurrency(tributosIncidentes)
  );

  const salvarBaseCalculo = async () => {
    setIsButtonLoading(true);
    if (codigoNCM === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione um código NCM',
      });
      setIsButtonLoading(false);
      return false;
    }

    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });
      setIsButtonLoading(false);
      return false;
    }

    if (hasPrecoBanhoEspecificado) {
      if (validarCamposObrigatoriosComBanho(valorBanho, quantidade, mostrarNotificacao) === false) return;
      const dadosComBanho: IBalnearioComBanhoSalvar = {
        idGuiaRecolhimento: guiaRecolhimentoId,
        id: idBalneario !== -1 ? idBalneario : undefined,
        codigoNcm: codigoNCM,
        valorBanho: removeFormatCurrency(valorBanho),
        qtdeBanho: parseFloat(quantidade),
      };

      if (!isEdicao) {
        await postSalvarBalnearioComBanho(dadosComBanho)
          .then(() => {
            setIsEdicao(false);
            setIsAdicaoNovaBaseCalculo(false);
          })
          .catch((err: IErrorWithSentryId) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          )
          .finally(() => setIsButtonLoading(false));
      } else {
        await putAtualizarBalnearioComBanho(dadosComBanho)
          .then(() => {
            setIsEdicao(false);
            setIsAdicaoNovaBaseCalculo(false);
          })
          .catch((err: IErrorWithSentryId) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          )
          .finally(() => setIsButtonLoading(false));
      }
    } else {
      if (
        validarCamposObrigatoriosSemBanho(
          receitaBrutaEstabelecimento,
          deducoesAdicionadas,
          converterDeducoesParaArrayString(deducoesData),
          mostrarNotificacao
        ) === false
      ) {
        setIsButtonLoading(false);
        return;
      }
      if (removeFormatCurrency(receitaLiquidaEstabelecimento) < 0) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Receita líquida do Estabelecimento não pode ser negativa.',
        });
        setIsButtonLoading(false);
        return false;
      }
      const dadosSemBanho: IBalnearioSemBanhoSalvar = {
        idGuiaRecolhimento: guiaRecolhimentoId,
        id: idBalneario !== -1 ? idBalneario : undefined,
        codigoNcm: codigoNCM,
        valorReceitaBruta: removeFormatCurrency(receitaBrutaEstabelecimento),
        tributos: deducoesAdicionadas,
      };
      if (!isEdicao) {
        await postSalvarBalnearioSemBanho(dadosSemBanho)
          .then(() => {
            setIsEdicao(false);
            setIsAdicaoNovaBaseCalculo(false);
          })
          .catch((err: IErrorWithSentryId) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          )
          .finally(() => setIsButtonLoading(false));
      } else {
        await putAtualizarBalnearioSemBanho(dadosSemBanho)
          .then(() => {
            setIsEdicao(false);
            setIsAdicaoNovaBaseCalculo(false);
          })
          .catch((err: IErrorWithSentryId) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          )
          .finally(() => setIsButtonLoading(false));
      }
    }
  };

  const receberInformacaoBalnearioSemBanho = async () => {
    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });

      return;
    }

    if (
      !validarCamposObrigatoriosSemBanho(
        receitaBrutaEstabelecimento,
        deducoesAdicionadas,
        converterDeducoesParaArrayString(deducoesData),
        mostrarNotificacao
      )
    ) {
      return;
    }

    if (removeFormatCurrency(receitaLiquidaEstabelecimento) < 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Receita líquida do Estabelecimento não pode ser negativa.',
      });
      return;
    }
    setIsLoadingInfo(true);
    const body: IBalnearioInformacoesSemBanhoEnviar = {
      valorReceitaBruta: removeFormatCurrency(receitaBrutaEstabelecimento),
      deducoes: deducoesAdicionadas,
    };
    await postReceberInformacaoBalnearioSemBanho(body)
      .then((res) => {
        setInfoSemBanho([{ label: 'Base de Cálculo(R$)', value: res.valorBaseDeCalculo }]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
        setIsLoadingInfo(false);
      });
  };

  const receberInformacaoBalnearioComBanho = async () => {
    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });

      return;
    }

    if (!validarCamposObrigatoriosComBanho(valorBanho, quantidade, mostrarNotificacao)) {
      return;
    }

    setIsLoadingInfo(true);
    const body: IBalnearioInformacoesComBanhoEnviar = {
      valorTotalBanho: removeFormatCurrency(valorBanho),
      quantidade: parseFloat(quantidade),
    };
    await postReceberInformacaoBalnearioComBanho(body)
      .then((res) => {
        setInfoComBanho([{ label: 'Base de Cálculo(R$)', value: res.valorBaseDeCalculo }]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });

        setIsLoadingInfo(false);
      });
  };

  return (
    <div className={styles.container}>
      {balnearioDataError || deducoesError ? (
        <ErrorMessage
          errorMessage={'Erro ao carregar dados do Balneário. Por favor, tente novamente.'}
          onClickFunction={() => {
            setIsEdicao(false);
            setIsAdicaoNovaBaseCalculo(false);
          }}
        />
      ) : (
        <>
          {balnearioDataLoading && isEdicao ? (
            <div className={styles.loadingContainer} data-testid='loading-container'>
              <BrLoading large />
            </div>
          ) : (
            <>
              <div>
                <div className={styles.titleContainer}>
                  <h4 className={styles.title}>{isEdicao ? 'Editar ' : 'Nova '}Base de Cálculo</h4>
                  <span className={styles.subTitle}>Operação - Balneário</span>
                </div>
                <div className={styles.dadosTriadeContainer}>
                  <Row>
                    <Col>
                      <span className={styles.label}>Competência</span>
                      <p className={styles.text}>{competencia}</p>
                    </Col>
                    <Col>
                      <span className={styles.label}>Processo Minerário</span>
                      <p className={styles.text}>{formatarNrProcesso(context?.numeroProcesso || '')}</p>
                    </Col>
                    <Col>
                      <span className={styles.label}>Substância</span>
                      <p className={styles.text}>{context?.substancia.toString()}</p>
                    </Col>
                    <Col>
                      <span className={styles.label}>Município/UF</span>
                      <p className={styles.text}>
                        {context?.municipio.nome}/{context?.municipio.uf}
                      </p>
                    </Col>
                  </Row>
                </div>
                <Row>
                  <Col>
                    <AnmSelectQuery
                      label='Código NCM'
                      value={codigoNCM}
                      setValue={setCodigoNCM}
                      functionQuery={() => getNcmPorGuia(guiaRecolhimentoId)}
                      keyQuery={['getNcmBalneario']}
                      formatResult={(result: INcmListagem[]) =>
                        result.map((ncm: INcmListagem) => ({
                          label: ncm.codigo + ' - ' + ncm.descricao,
                          value: ncm.codigo,
                        }))
                      }
                      placeholder='Selecione o NCM'
                      id='select-ncm-balneario'
                      hintText={getHintText('NCM')}
                    />
                  </Col>
                  <Col className={styles.switchContainer}>
                    <span className={styles.label}>Há Preço de Banho Especificado?</span>
                    {isEdicao ? (
                      <label className={styles.text}>{hasPrecoBanhoEspecificado ? 'Sim' : 'Não'}</label>
                    ) : (
                      <div
                        className={`br-switch icon ${styles.switch}`}
                        role='switch'
                        aria-checked={hasPrecoBanhoEspecificado}
                        tabIndex={0}
                        onClick={() => setHasPrecoBanhoEspecificado(!hasPrecoBanhoEspecificado)}
                        onKeyDown={() => setHasPrecoBanhoEspecificado(!hasPrecoBanhoEspecificado)}
                      >
                        <input
                          id='switch-icon'
                          type='checkbox'
                          name='switch-icon'
                          disabled={true}
                          checked={hasPrecoBanhoEspecificado}
                          role='switch'
                        />
                        <label className={styles.text}>{hasPrecoBanhoEspecificado ? 'Sim' : 'Não'}</label>
                      </div>
                    )}
                  </Col>
                </Row>
                <div className={styles.metodoContainer}>
                  <div className={styles.metodoTitleContainer}>
                    <span className={styles.metodoTitle}>
                      {hasPrecoBanhoEspecificado
                        ? 'Método com especificação do preço do banho'
                        : 'Método sem especificação do preço do banho'}
                    </span>
                  </div>
                  {hasPrecoBanhoEspecificado ? (
                    <div className={styles.metodoForm}>
                      <Row align-items-center>
                        <Col auto>
                          <div className={styles.label}>Valor Total do Banho (R$)</div>
                          <BrInput
                            autoComplete='off'
                            numeric={{
                              prefix: 'R$ ',
                              thousandSeparator: '.',
                              decimalScale: 2,
                              fixedDecimalScale: false,
                            }}
                            className={styles.input}
                            value={valorBanho}
                            onChange={(e) => setValorBanho(e.target.value)}
                            data-testid='input-valor-banho'
                          />
                        </Col>
                        <Col size={3} auto>
                          <div className={styles.label}>Quantidade</div>
                          <BrInput
                            className={styles.input}
                            autoComplete='off'
                            numeric={{
                              decimalScale: 2,
                              fixedDecimalScale: false,
                            }}
                            value={quantidade}
                            onChange={(e) => setQuantidade(e.target.value.replace(',', '.'))}
                            data-testid='input-quantidade'
                          />
                        </Col>
                        <Col className={styles.baseCalculoContainer} auto>
                          <div className={styles.label}>Preço Médio (R$)</div>
                          <div className={styles.textBaseCalculo}>
                            <span className={styles.text}>
                              {valorBanho && quantidade && isCalculoInvalido === false
                                ? formatCurrency(removeFormatCurrency(valorBanho) / parseFloat(quantidade))
                                : '-'}
                            </span>
                          </div>
                        </Col>
                        <Col auto>
                          <AtualizarInformacoes
                            onClickFunction={() => receberInformacaoBalnearioComBanho()}
                            info={infoComBanho}
                            widthFitContent={false}
                            isLoading={isLoadingInfo}
                          />
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <>
                      <div className={styles.metodoForm}>
                        <Row>
                          <Col>
                            <div className={styles.label}>Receita Bruta do Estabelecimento (R$)</div>
                            <BrInput
                              autoComplete='off'
                              numeric={{
                                prefix: 'R$ ',
                                thousandSeparator: '.',
                                decimalScale: 2,
                                fixedDecimalScale: false,
                              }}
                              className={styles.input}
                              value={receitaBrutaEstabelecimento}
                              onChange={(e) => setReceitaBrutaEstabelecimento(e.target.value)}
                              data-testid='input-receita-bruta'
                            />
                          </Col>
                          <Col></Col>
                        </Row>
                      </div>
                      {deducoesIsLoading || balnearioDataLoading ? (
                        <BrLoading />
                      ) : (
                        <SecaoDeducoes
                          key={codigoNCM}
                          deducoesPossiveis={deducoesData}
                          deducoesAdicionadas={deducoesAdicionadas}
                          setDeducoesAdicionadas={setDeducoesAdicionadas}
                          setHaCompraTerceiro={setHaCompraTerceiro}
                          haCompraTerceiro={haCompraTerceiro}
                          semMargem={false}
                        />
                      )}
                      <div className={styles.metodoDivider}>
                        <BrDivider />
                      </div>
                      <div className={styles.infoContainer}>
                        <div>
                          <span className={styles.label}>Receita líquida do estabelecimento (R$)</span>
                          {isCalculoInvalido === true && <p className={styles.text}> - </p>}
                          {isCalculoInvalido === false && (
                            <p className={styles.text}> {receitaLiquidaEstabelecimento}</p>
                          )}
                        </div>
                        <div>
                          <AtualizarInformacoes
                            onClickFunction={() => receberInformacaoBalnearioSemBanho()}
                            info={infoSemBanho}
                            widthFitContent={false}
                            isLoading={isLoadingInfo}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.containerButton}>
                <BrButton
                  secondary
                  onClick={() => {
                    setIsEdicao(false);
                    setIsAdicaoNovaBaseCalculo(false);
                  }}
                >
                  <span className={styles.button}>
                    <i className={`fas fa-times`} /> Cancelar
                  </span>
                </BrButton>
                <BrButton onClick={() => salvarBaseCalculo()} primary loading={isButtonLoading}>
                  <span className={styles.button}>
                    {isEdicao ? (
                      <>
                        <i className={`fas fa-save`} /> Salvar Base de Cálculo
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                      </>
                    )}
                  </span>
                </BrButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BalnearioForm;
